import React, { useEffect, useState } from "react";
import { Link as RouterLink, useNavigate, useParams } from "react-router-dom";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import CircularProgress from "@mui/material/CircularProgress";
import Typography from "@mui/material/Typography";
import WineTastingGridView from "../components/wine/WineTastingGridView";
import { deleteTasting, getTasting } from "../utils/apiClient";
import useDocumentTitle from "../hooks/useDocumentTitle";

function WineTastingDetail() {
  const { tastingUuid } = useParams();
  const navigate = useNavigate();
  const [tasting, setTasting] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [deleting, setDeleting] = useState(false);

  useDocumentTitle(tasting?.title || "Smagning");

  useEffect(() => {
    let cancelled = false;
    (async () => {
      setLoading(true);
      setError("");
      try {
        const data = await getTasting(tastingUuid);
        if (!cancelled) setTasting(data);
      } catch (err) {
        const detail = err.response?.data?.detail;
        if (!cancelled) {
          setError(typeof detail === "string" ? detail : "Smagning ikke fundet");
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [tastingUuid]);

  const handleDelete = async () => {
    if (!window.confirm("Slet denne smagning?")) return;
    setDeleting(true);
    try {
      await deleteTasting(tastingUuid);
      navigate("/wine-tasting");
    } catch (err) {
      const detail = err.response?.data?.detail;
      setError(typeof detail === "string" ? detail : "Kunne ikke slette");
      setDeleting(false);
    }
  };

  if (loading) {
    return (
      <Box sx={{ display: "flex", justifyContent: "center", py: 6 }}>
        <CircularProgress size={28} />
      </Box>
    );
  }

  if (error || !tasting) {
    return (
      <Box sx={{ py: 2 }}>
        <Button component={RouterLink} to="/wine-tasting" sx={{ px: 0, mb: 1 }}>
          Tilbage
        </Button>
        <Typography color="error">{error || "Ikke fundet"}</Typography>
      </Box>
    );
  }

  const payload = tasting.payload || {};
  const isNotesOnly = payload.kind === "notes" || (payload.notes && !payload.answers);
  const isBlind = payload.blind !== false;

  return (
    <Box sx={{ py: { xs: 1, sm: 2 } }}>
      <Button component={RouterLink} to="/wine-tasting" sx={{ px: 0, mb: 1 }}>
        Tilbage
      </Button>
      <Typography variant="h5" sx={{ fontWeight: 700, mb: 0.5 }}>
        {tasting.title}
      </Typography>
      {tasting.created_at ? (
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          {new Date(tasting.created_at).toLocaleString()}
        </Typography>
      ) : null}

      {isNotesOnly ? (
        <Box sx={{ mb: 2 }}>
          {payload.wine ? (
            <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
              {[payload.wine.producer, payload.wine.name, payload.wine.vintage]
                .filter(Boolean)
                .join(" · ")}
            </Typography>
          ) : null}
          <Typography variant="body1" sx={{ whiteSpace: "pre-wrap" }}>
            {payload.notes || ""}
          </Typography>
        </Box>
      ) : (
        <WineTastingGridView
          answers={payload.answers || {}}
          wine={payload.wine || null}
          mode="all"
          blind={isBlind}
          title="Deduktiv smagning"
          comparisons={payload.comparisons || null}
          expected={payload.expected || null}
        />
      )}

      <Button color="error" onClick={handleDelete} disabled={deleting} sx={{ mt: 2 }}>
        {deleting ? "Sletter…" : "Slet smagning"}
      </Button>
    </Box>
  );
}

export default WineTastingDetail;
